/**
 * Application shell — top bar + canvas slot.
 *
 * Composes :class:`TopBar` above an ``<Outlet />`` that the router
 * populates with each route's page component. The canvas slot is
 * intentionally unstructured: Stream C's PR fills it with the
 * dockview multi-pane workspace; the shell doesn't care.
 *
 * The left rail was removed 2026-05-29 per user feedback — navigation
 * is the multi-pane workspace's panel tabs + add-panel menu
 * (Ctrl+Shift+P), not a rail of mutually-exclusive surfaces (the
 * pre-ADR-024 model). See the chrome-spec.md amendment.
 *
 * Component-level canvas independence per ADR-022 Implementation
 * Notes — every primary surface (Map, Platforms, Events,
 * Audit, Settings) is a self-contained component that does not
 * assume canvas ownership; chrome is separable from the canvas.
 *
 * The Profile editor drawer (ADR-033 §5) is mounted here too — a
 * right-docked, non-modal ``<aside>`` overlaying the canvas at the right
 * edge. It renders nothing until the gear menu opens an editor session
 * (``profile-editor-store``), so the shell is observation-free until then.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/shared/chrome-spec.md
 *     (2026-05-29 amendment — left rail retired).
 *   - ADR-022 (component-level canvas independence).
 *   - ADR-024 §4 (multi-pane workspace mounts inside the canvas).
 *   - ADR-033 §5 / REQ-UI-076 (Profile editor drawer).
 */
import { Outlet } from "react-router";
import { BannerHost } from "@/notifications/banner-host";
import { ToastHost } from "@/notifications/toast-host";
import { AnnouncerProvider } from "@/chrome/announcer";
import { TopBar } from "@/chrome/top-bar";
import { ProfileEditor } from "@/profiles/profile-editor";
import { useRevealMapSelectionInList } from "@/selection/use-reveal-map-selection";

export function AppShell(): React.ReactElement {
  // Map→list reveal (REQ-UI-051): selecting a glyph on the map surfaces the
  // matching Platforms / Detections / Alerts list tab if it's behind another
  // tab, so the synced selection is visible without a manual switch. Mounted
  // here (under the MultiPaneApiProvider) so it spans every authenticated
  // route; a no-op on routes without a workspace.
  useRevealMapSelectionInList();

  // ``AnnouncerProvider`` wraps the shell so the screen-reader
  // live region is mounted alongside every authenticated route.
  // The :class:`MultiPaneWorkspace` consumes it via
  // :func:`useAnnouncer` to announce panel add / remove /
  // layout reset / maximize / restore events per REQ-UI-059.
  // Stream A PR 5 (selection bus) and Stream A PR 6 (display-
  // only mode) will add more producers later.
  return (
    <AnnouncerProvider>
      {/* ``h-screen`` — the shell owns the viewport height directly. (It was
       * ``h-full`` when the app-root ClassificationFrame owned the height and
       * framed the shell with top/bottom banners; the banner was replaced by a
       * top-bar badge 2026-06-12, so the shell is now the top of the
       * authenticated render tree and owns its own height.) */}
      <div className="flex h-screen flex-col bg-background text-foreground">
        <TopBar />
        {/* CRITICAL sticky banner (Flow F Frame 4 / ADR-029 §5). Mounted
         * FIRST after the topbar deliberately: it is announced via
         * ``role="alert"`` and does NOT trap focus (feature-spec §0.1 D3 —
         * ratified departure from Flow F, since trapping focus in a
         * non-modal is a WCAG 2.1.2 hazard), so DOM order is what makes it
         * the next thing a keyboard operator reaches. Renders nothing when
         * no critical condition is live. */}
        <BannerHost />
        <main
          role="main"
          aria-label="Main canvas"
          // ``overflow-hidden`` so dockview-react's grid owns
          // scrolling within each panel — Stream C PR 1 per
          // the chrome-spec.md 2026-05-26 amendment. The auth
          // / not-found pages are centered + non-scrolling so
          // the change is observation-free for them. With the left
          // rail retired (2026-05-29) the canvas fills the full width
          // below the top bar.
          className="flex-1 overflow-hidden"
          data-testid="canvas-slot"
        >
          <Outlet />
        </main>
        {/* Profile editor drawer (ADR-033 §5 / REQ-UI-076) — renders
         * nothing until the gear menu opens a session. Mounted at the
         * shell level (not inside the canvas) so it overlays the full
         * height at the right edge, canvas staying live behind it. */}
        <ProfileEditor />
        {/* Bottom-right toast layer (Flow F Frames 2-3 / chrome-spec.md
         * §"Toast layer"). Last in the shell so it paints above the canvas;
         * the layer itself is pointer-events-none so it never becomes a
         * dead zone over the operator's map. Renders nothing when idle. */}
        <ToastHost />
      </div>
    </AnnouncerProvider>
  );
}
